import { BaseAction } from '@cotera/client/v0/actions/framework/action.base';
import type {
  ActionConstructor,
  ActionResult,
  ApplicableContext,
  AskResponse,
  ExecuteContext,
  TargetableModel,
} from '@cotera/client/v0/actions/framework/types';
import { COLUMN_ACTION_GROUP } from '@cotera/client/v0/columns/constants';
import { z } from 'zod/v3';
import { Assert } from '@cotera/utilities';
import { ModalPageView } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Icon } from '@cotera/client/app/components/ui/icon';
import {
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { EventStreamDefinition } from '@cotera/sdk/core';

type SnapshotModel = TargetableModel & {
  datasetName: string;
  currentNameInGraph: Watchable<string>;
  isNew: boolean;
  eventStream: ReadonlyWatchable<EventStreamDefinition | null>;
  changes: ReadonlyWatchable<Record<string, boolean | string>>;
  hasErrors: ReadonlyWatchable<boolean>;
};
type ModelConstructor = new (...args: any[]) => SnapshotModel;

export const makeRunColumnAction = (
  title: string,
  modelConstructor: ModelConstructor,
  runAllActionConstructor: ActionConstructor<{}, any>,
  runNewActionConstructor: ActionConstructor<{}, any>
) => {
  return class extends BaseAction<
    {
      mode: 'all' | 'new';
    },
    { t: string; message?: string }[]
  > {
    title = title;
    inputSchema = z.object({
      mode: z.enum(['all', 'new']),
    });
    group = COLUMN_ACTION_GROUP;
    icon = 'forward';

    constructor(
      private readonly modalManager: {
        pop: () => void;
        push: (props: {
          view: () => React.ReactNode;
          onClose?: () => void;
        }) => void;
      }
    ) {
      super();
    }

    applicable(context: ApplicableContext): boolean {
      const model = getTargetedModel(context, modelConstructor);

      if (model === null || model.hasErrors.snapshot()) {
        return false;
      }

      // Check that eventStream is available (not null)
      // This prevents the action from being applicable when the column
      // hasn't been built yet or the graph is in an invalid state
      const eventStream = model.eventStream.snapshot();
      return eventStream !== null;
    }

    async execute(
      payload: {
        mode: 'all' | 'new';
      },
      context: ExecuteContext
    ): Promise<ActionResult<{ t: string; message?: string }[]>> {
      if (payload.mode === 'all') {
        return context
          .getAction(runAllActionConstructor)!
          .askOrExecute({}, context);
      } else {
        return context
          .getAction(runNewActionConstructor)!
          .askOrExecute({}, context);
      }
    }

    shouldAsk(_context: ApplicableContext): boolean {
      return true;
    }

    ask(
      _: {},
      context: ExecuteContext
    ): AskResponse<{
      mode: 'all' | 'new';
    }> {
      const model = getTargetedModel(context, modelConstructor);

      Assert.assert(model !== null);

      return {
        view: ({ onRespond, onCancel }) => {
          this.modalManager.push({
            view: () => (
              <AskView
                onRespond={async (params) => {
                  await onRespond({ mode: params.mode });
                  this.modalManager.pop();
                }}
                model={model}
              />
            ),
            onClose: async () => {
              await onCancel();
            },
          });
        },
      };
    }
  };
};

const AskView: React.FC<{
  onRespond: (params: { mode: 'all' | 'new' }) => Promise<void>;
  model: SnapshotModel;
}> = ({ onRespond, model }) => {
  return (
    <ModalPageView
      title={`Run ${model.currentNameInGraph.snapshot()}`}
      subtitle={`Do you want to run from the beginning or only on new rows that haven't been run yet?`}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={async () => {
              await onRespond({
                mode: 'all',
              });
            }}
          >
            Run on all rows
            <Icon icon="play" size="small" />
          </Button>
          <Button
            variant="default"
            onClick={async () => {
              await onRespond({ mode: 'new' });
            }}
          >
            Run on new rows
            <Icon icon="play" size="small" />
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};

const getTargetedModel = (
  context: Pick<ExecuteContext, 'targetedModelOfType'>,
  modelConstructor: ModelConstructor
): SnapshotModel | null => {
  const model = context.targetedModelOfType(modelConstructor);

  return model;
};
